import React, { useState, useEffect } from 'react';
import { X, Shield } from 'lucide-react';
import Button from '../common/Button';
import { USER_ROLES } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';

const PromoteUserModal = ({ isOpen, onClose, onSubmit, user, loading }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (user) {
      setSelectedRole(user.role);
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(user.id, selectedRole);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-600" />
              <h3 className="text-xl font-semibold text-gray-900">Change User Role</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Change role for <strong>{user.full_name}</strong>
              </p>
              
              <div className="space-y-3">
                {[
                  { value: USER_ROLES.USHER, label: 'Event Usher', desc: 'Can register attendees, check-in, and print badges' },
                  { value: USER_ROLES.FRONT_DESK, label: 'Front Desk Staff', desc: 'Can register attendees and manage check-ins' },
                  { value: USER_ROLES.EVENT_ADMIN, label: 'Event Administrator', desc: 'Can manage events and attendees' },
                  // Only show Super Admin option if current user is Super Admin
                  ...(currentUser?.role === USER_ROLES.SUPER_ADMIN ? [
                    { value: USER_ROLES.SUPER_ADMIN, label: 'Super Administrator', desc: 'Full system access' }
                  ] : []),
                  { value: USER_ROLES.ATTENDEE, label: 'Attendee (Kiosk)', desc: 'Self-registration only (for kiosk mode)' },
                ].map((role) => (
                  <label
                    key={role.value}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedRole === role.value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role.value}
                      checked={selectedRole === role.value}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">{role.label}</div>
                      <div className="text-sm text-gray-500">{role.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
                disabled={selectedRole === user.role}
              >
                Change Role
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PromoteUserModal;
